(() => {
  const config = window.AiryssProtectionPlan;
  if (!config?.enabled || !config.variantId) return;

  const CARD_ID = 'airyss-protection-card';
  const WRAPPER_ID = 'airyss-protection-card-wrap';
  const STYLE_ID = 'airyss-protection-upcart-styles';
  const PROPERTY_KEY = '_protection_plan';
  const PARENT_PROPERTY = '_protection_for';
  let busy = false;
  let syncTimer = null;
  let upcartApiBound = false;
  let shadowObserverBound = false;
  let cachedVariantId = config.variantId ? Number(config.variantId) : null;
  let optimisticAdded = null; // true/false while a cart mutation is in flight
  let lastCart = null;
  let paintTimer = null;
  let stableUiUntil = 0; // suppress re-mount / flicker after add/remove
  let lastProtectionCents = 2900;

  // UpCart V2 renders inside a shadow root. Always query through this helper.
  const upcartRoot = () => window.upcartDocumentOrShadowRoot || document;

  const readUpcartCartSync = () => {
    if (typeof window.upcartGetCart !== 'function') return null;
    try {
      const cart = window.upcartGetCart();
      return cart?.items ? cart : null;
    } catch (error) {
      return null;
    }
  };

  const getCart = () => {
    const upcartCart = readUpcartCartSync();
    if (upcartCart) {
      lastCart = upcartCart;
      return Promise.resolve(upcartCart);
    }
    return fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json()).then((cart) => {
      lastCart = cart;
      return cart;
    });
  };

  const refreshCartDrawers = ({ soft = false } = {}) => {
    // Soft refresh: only ask UpCart to reload totals/lines. Avoid broadcasting
    // many theme events that restack observers and make the drawer feel laggy.
    if (typeof window.upcartRefreshCart === 'function') {
      try { window.upcartRefreshCart(); } catch (error) { /* ignore */ }
    }
    if (soft) return;
    if (window.publish && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'protection-plan-upsell' });
    }
    ['cart:refresh', 'cart:updated', 'upcart:refresh', 'upcart:cart:updated'].forEach((eventName) => {
      window.dispatchEvent(new CustomEvent(eventName));
      document.dispatchEvent(new CustomEvent(eventName));
    });
  };

  const parseMoneyToCents = (text) => {
    const raw = String(text || '').replace(/[^\d.,-]/g, '');
    if (!raw) return null;
    const normalized = raw.includes(',') && raw.includes('.')
      ? raw.replace(/,/g, '')
      : raw.replace(/,/g, '.');
    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  };

  const formatCents = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  // Instantly nudge UpCart subtotal/checkout labels so the UI doesn't wait on refresh.
  // IMPORTANT: never pass a "$..." string as replace()'s replacement — "$1" is treated as
  // a capture-group backreference and turns "$178.99" into "$78.99" (the missing "1" flicker).
  const replaceMoneyText = (text, formatted) => {
    const moneyRe = /[€£$]\s?[\d,]+(?:\.\d{2})?/;
    if (!moneyRe.test(text)) return formatted;
    return text.replace(moneyRe, () => formatted);
  };

  const bumpVisibleTotals = (deltaCents) => {
    if (!deltaCents) return;
    const root = upcartRoot();
    const nodes = [
      ...(root.querySelectorAll?.('[class*="ubtotal"], [class*="Subtotal"], [class*="checkout"], [class*="Checkout"], [class*="total"], [class*="Total"]') || []),
      ...document.querySelectorAll('[class*="ubtotal"], [class*="checkout"], [class*="CartTotal"]'),
    ];
    const moneyRe = /[€£$]\s?[\d,]+(?:\.\d{2})?/;
    const touched = new Set();
    nodes.forEach((node) => {
      if (!node || node.closest?.(`#${CARD_ID}, #airyss-included-gift-card`)) return;
      const targets = [];
      if (!node.children?.length) {
        targets.push(node);
      } else {
        const moneyChild = [...node.querySelectorAll('*')].find((el) => {
          const t = (el.textContent || '').trim();
          return !el.children?.length && moneyRe.test(t) && t.length <= 24;
        });
        if (moneyChild) targets.push(moneyChild);
      }
      targets.forEach((el) => {
        if (touched.has(el)) return;
        const text = (el.textContent || '').trim();
        if (!moneyRe.test(text) || text.length > 48) return;
        const cents = parseMoneyToCents(text);
        if (cents == null || cents < 100) return; // skip junk like $0.00 noise
        const next = Math.max(0, cents + deltaCents);
        const formatted = formatCents(next);
        el.textContent = replaceMoneyText(text, formatted);
        touched.add(el);
      });
    });
  };

  const holdStableUi = (ms = 1800) => {
    stableUiUntil = Date.now() + ms;
  };

  const holdCartCount = (delta, ms = 2200) => {
    const cart = lastCart || readUpcartCartSync();
    const base = window.AiryssCartCount?.displayCount?.(cart)
      ?? (cart?.item_count ?? cart?.items?.length ?? 0);
    // When adding, current cart may not include protection yet — apply delta.
    // When removing, current cart may still include it — apply delta.
    const next = Math.max(0, Number(base) + Number(delta || 0));
    if (window.AiryssCartCount?.hold) window.AiryssCartCount.hold(next, ms);
  };

  const releaseCartCountHold = () => {
    if (window.AiryssCartCount?.release) window.AiryssCartCount.release();
    const cart = lastCart || readUpcartCartSync();
    if (cart && window.AiryssCartCount?.sync) window.AiryssCartCount.sync(cart);
  };

  const isParentItem = (item) => {
    if (!item || item.properties?.[PROPERTY_KEY] === 'true') return false;
    if (item.properties?._free_gift === 'true') return false;
    const handle = String(item.handle || item.product_handle || item.productHandle || '').toLowerCase();
    const title = String(item.product_title || item.productTitle || item.title || '').toLowerCase();
    const sku = String(item.sku || '').toUpperCase();
    const url = String(item.url || item.product_url || '').toLowerCase();
    if (/protection|warranty|portable power|surface mount|magnetic|free gift/i.test(title)) return false;
    if (/free-gift|protection|warranty|portable|surface-mount|magnetic/i.test(handle)) return false;
    const handles = (config.parentHandles || []).map((h) => String(h).toLowerCase());
    if (handles.some((h) => h && (handle === h || handle.includes(h) || url.includes(`/products/${h}`)))) return true;
    if (/airyss\s*one/i.test(title)) return true;
    if (/airyss-one/i.test(handle) || /\/products\/airyss-one/i.test(url)) return true;
    if (/^AIRYSS-ONE/.test(sku)) return true;
    return false;
  };

  const isProtectionItem = (item) => {
    if (!item) return false;
    if (String(item.variant_id || item.variantId) === String(config.variantId)) return true;
    if (item.properties?.[PROPERTY_KEY] === 'true') return true;
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const title = String(item.product_title || item.title || '').toLowerCase();
    const sku = String(item.sku || '').toUpperCase();
    if (handle === String(config.productHandle || '2-year-accidental-protection-plan').toLowerCase()) return true;
    if (/2-year accidental protection|accidental protection plan/.test(title)) return true;
    if (sku === 'AIRYSS-PROTECT-2YR') return true;
    return false;
  };

  const moneyLabel = () => config.priceLabel || '+ $29';

  const shieldIconSvg = () => `
    <span class="airyss-protection-card__shield-badge" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="26" height="26" focusable="false" fill="none">
        <path d="M12 3l7 3v6c0 5-3.5 8.5-7 9.5C8.5 20.5 5 17 5 12V6l7-3z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
        <path d="M9.2 12.1l1.8 1.8 3.8-3.9" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
  `;

  const ensureUpcartStyles = (root) => {
    const host = root.head || root;
    if (!host?.appendChild) return;
    let style = host.querySelector?.(`#${STYLE_ID}`);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      host.appendChild(style);
    }
    style.textContent = `
      #${WRAPPER_ID},
      #airyss-included-gift-wrap {
        list-style: none !important;
        display: block !important;
        margin: 0 !important;
        padding: 0.15rem 0 !important;
        border: 0 !important;
        background: transparent !important;
        width: 100% !important;
      }
      #airyss-included-gift-wrap { order: 9998 !important; }
      #${WRAPPER_ID} {
        order: 9999 !important;
        position: relative !important;
        z-index: 10050 !important;
        pointer-events: auto !important;
      }
      #${CARD_ID} {
        margin: 0 12px 8px;
        padding: 14px 14px;
        border: 1px solid #e6e6e6;
        border-radius: 16px;
        background: #fff;
        color: #111;
        font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        box-sizing: border-box;
        cursor: pointer;
        user-select: none;
        position: relative !important;
        z-index: 10051 !important;
        pointer-events: auto !important;
      }
      #${CARD_ID} * { pointer-events: none !important; }
      #${CARD_ID} .airyss-protection-card__native {
        pointer-events: auto !important;
        position: absolute;
        inset: 0;
        opacity: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        cursor: pointer;
        z-index: 2;
      }
      #${CARD_ID}.is-added {
        border-color: #b7dfc4;
        background: #f3fbf5;
      }
      #${CARD_ID} .airyss-protection-card__row {
        display: grid;
        grid-template-columns: 22px 48px 1fr auto;
        gap: 12px;
        align-items: center;
      }
      #${CARD_ID} .airyss-protection-card__checkbox {
        width: 22px; height: 22px;
        border: 1.5px solid #c5c5c5; border-radius: 6px;
        display: inline-flex; align-items: center; justify-content: center;
        background: #fff; flex: 0 0 22px;
      }
      #${CARD_ID}.is-added .airyss-protection-card__checkbox {
        border-color: #1f9d4b;
        background: #22a34a;
        color: #fff;
      }
      #${CARD_ID} .airyss-protection-card__checkbox svg { width: 12px; height: 12px; display: none; }
      #${CARD_ID}.is-added .airyss-protection-card__checkbox svg { display: block; }
      #${CARD_ID} .airyss-protection-card__shield {
        display: inline-flex; color: #333;
      }
      #${CARD_ID} .airyss-protection-card__shield-badge {
        width: 48px; height: 48px; border-radius: 50%;
        background: #f2f2f2;
        display: inline-flex; align-items: center; justify-content: center;
        flex: 0 0 48px;
      }
      #${CARD_ID}.is-added .airyss-protection-card__shield { color: #1f9d4b; }
      #${CARD_ID}.is-added .airyss-protection-card__shield-badge { background: #e7f6ec; }
      #${CARD_ID} .airyss-protection-card__title {
        margin: 0 0 .2rem; font-size: 16px; font-weight: 700; line-height: 1.3;
      }
      #${CARD_ID} .airyss-protection-card__desc {
        margin: 0; font-size: 14px; line-height: 1.4; color: #666;
      }
      #${CARD_ID} .airyss-protection-card__price {
        margin: 0; font-size: 16px; font-weight: 700; white-space: nowrap; padding-top: 1px;
      }
      #${CARD_ID}[hidden] { display: none !important; }
      .airyss-protection-line-hidden,
      .airyss-legacy-benefit-hidden,
      .airyss-native-gift-line-hidden {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        border: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      /* Instant hide — do not wait for JS after UpCart paints the protection line */
      .upcart-public-cart-item:has(a[href*="2-year-accidental-protection-plan"]),
      .upcart-internal-cart-item:has(a[href*="2-year-accidental-protection-plan"]),
      .upcart-public-cart-item:has(a[href*="/products/2-year-accidental-protection"]),
      .upcart-internal-cart-item:has(a[href*="/products/2-year-accidental-protection"]) {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        border: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
  };

  // UpCart lives in a shadow root. Document-level event.target is retargeted to
  // the host, so closest() misses the card — always walk composedPath first.
  const findProtectionCard = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (let i = 0; i < path.length; i += 1) {
      const node = path[i];
      if (!node || node.nodeType !== 1) continue;
      if (node.id === CARD_ID || node.getAttribute?.('data-airyss-protection-card') === 'true') {
        return node;
      }
      const nested = node.closest?.(`#${CARD_ID}, [data-airyss-protection-card]`);
      if (nested) return nested;
    }
    const target = event.target;
    if (target?.getRootNode) {
      const root = target.getRootNode();
      const fromRoot = root.getElementById?.(CARD_ID);
      if (fromRoot && (target === fromRoot || fromRoot.contains(target))) return fromRoot;
    }
    return event.target?.closest?.(`#${CARD_ID}, [data-airyss-protection-card]`) || null;
  };

  let lastActivateAt = 0;
  const activateProtectionToggle = (forceAdded) => {
    // Failsafe: never stay locked out if a prior request hung.
    if (busy && Date.now() - (handleProtectionActivate._busySince || 0) > 2500) busy = false;
    if (busy) return;
    const now = Date.now();
    if (now - lastActivateAt < 350) return;
    lastActivateAt = now;

    const card = ensureCard();
    const shouldAdd = typeof forceAdded === 'boolean'
      ? forceAdded
      : !card.classList.contains('is-added');
    if (shouldAdd) addProtection();
    else removeProtection();
  };

  const handleProtectionActivate = (event) => {
    const card = findProtectionCard(event) || (event.currentTarget?.id === CARD_ID ? event.currentTarget : null);
    if (!card) return;
    if (event.type === 'change' && event.target?.matches?.('.airyss-protection-card__native')) {
      event.stopPropagation();
      activateProtectionToggle(Boolean(event.target.checked));
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    activateProtectionToggle();
  };

  const wireCardActivation = (card) => {
    if (!card) return card;
    // Re-bind every paint — UpCart can clone/replace nodes and drop listeners.
    const native = card.querySelector('.airyss-protection-card__native');
    const onPointer = (event) => {
      // pointerup/mousedown often survive cart apps that only stopPropagation on click.
      if (event.type === 'mousedown' && event.button !== 0) return;
      handleProtectionActivate(event);
    };
    card.onclick = handleProtectionActivate;
    card.onpointerup = onPointer;
    card.onmousedown = onPointer;
    card.onkeydown = (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      handleProtectionActivate(event);
    };
    if (native) {
      native.onchange = handleProtectionActivate;
      native.onclick = (event) => {
        // Let the checkbox toggle, then sync cart from the change handler.
        event.stopPropagation();
      };
    }
    card.__airyssProtectionWired = true;
    return card;
  };

  const ensureCard = () => {
    const root = upcartRoot();
    let card = root.getElementById?.(CARD_ID) || document.getElementById(CARD_ID);
    // Migrate away from older protection card markup (button CTA / tiny shield / no native input).
    if (card && (
      !card.querySelector('[data-protection-checkbox]')
      || !card.querySelector('.airyss-protection-card__shield-badge')
      || !card.querySelector('.airyss-protection-card__native')
    )) {
      card.remove();
      root.getElementById?.(WRAPPER_ID)?.remove();
      document.getElementById(WRAPPER_ID)?.remove();
      card = null;
    }
    if (card) return wireCardActivation(card);

    card = document.createElement('div');
    card.id = CARD_ID;
    card.className = 'airyss-protection-card';
    card.setAttribute('data-airyss-protection-card', 'true');
    card.setAttribute('role', 'checkbox');
    card.setAttribute('tabindex', '0');
    card.hidden = true;
    card.innerHTML = `
      <input class="airyss-protection-card__native" type="checkbox" aria-label="Add 2-Year Accidental Protection Plan" tabindex="-1" />
      <div class="airyss-protection-card__row">
        <span class="airyss-protection-card__checkbox" data-protection-checkbox aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none"><path d="M3.5 8.2l2.8 2.8 6.2-6.4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
        <span class="airyss-protection-card__shield" data-protection-shield>${shieldIconSvg()}</span>
        <div class="airyss-protection-card__copy">
          <h3 class="airyss-protection-card__title" data-protection-title></h3>
          <p class="airyss-protection-card__desc" data-protection-desc></p>
        </div>
        <p class="airyss-protection-card__price" data-protection-price></p>
      </div>
    `;

    return wireCardActivation(card);
  };

  const bindProtectionActivation = (root) => {
    if (!root) return;
    // Always (re)attach capture listeners — UpCart may replace the shadow root.
    if (root.__airyssProtectionActivateHandler) {
      root.removeEventListener('click', root.__airyssProtectionActivateHandler, true);
      root.removeEventListener('pointerup', root.__airyssProtectionActivateHandler, true);
      root.removeEventListener('mousedown', root.__airyssProtectionActivateHandler, true);
    }
    root.__airyssProtectionActivateHandler = handleProtectionActivate;
    root.addEventListener('click', handleProtectionActivate, true);
    root.addEventListener('pointerup', handleProtectionActivate, true);
    root.addEventListener('mousedown', handleProtectionActivate, true);
    if (!root.__airyssProtectionKeyBound) {
      root.addEventListener('keydown', (event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        if (!findProtectionCard(event)) return;
        handleProtectionActivate(event);
      }, true);
      root.__airyssProtectionKeyBound = true;
    }
    root.__airyssProtectionActivateBound = true;
  };

  const ensureWrapper = (card) => {
    const root = upcartRoot();
    let wrap = root.getElementById?.(WRAPPER_ID) || document.getElementById(WRAPPER_ID);
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = WRAPPER_ID;
      wrap.className = 'airyss-protection-card-wrap';
      wrap.setAttribute('data-airyss-protection-wrap', 'true');
    }
    if (card.parentElement !== wrap) wrap.appendChild(card);
    return wrap;
  };

  const renderCardState = (card, added) => {
    const title = card.querySelector('[data-protection-title]');
    const desc = card.querySelector('[data-protection-desc]');
    const price = card.querySelector('[data-protection-price]');
    const errorNotice = card.querySelector('[data-protection-error]');
    const native = card.querySelector('.airyss-protection-card__native');

    card.classList.toggle('is-added', added);
    card.setAttribute('aria-checked', added ? 'true' : 'false');
    if (native && native.checked !== added) native.checked = added;
    if (errorNotice) errorNotice.remove();

    title.textContent = added
      ? (config.title || '2-Year Accidental Protection Plan')
      : (config.checkboxLabel || 'Add 2-Year Accidental Protection Plan');
    desc.textContent = added
      ? (config.addedDescription || 'Covered for drops, cracks and everyday accidents.')
      : (config.description || 'Protect your AIRYSS from drops, cracks and everyday accidents.');
    price.textContent = moneyLabel();
  };

  const queryAll = (selector) => {
    const root = upcartRoot();
    const fromRoot = [...(root.querySelectorAll?.(selector) || [])];
    if (root === document) return fromRoot;
    // Also check light DOM in case Upcart mirrors nodes.
    const fromDocument = [...document.querySelectorAll(selector)];
    return [...new Set([...fromRoot, ...fromDocument])];
  };

  const getUpCartItems = () => queryAll('.upcart-public-cart-item, .upcart-internal-cart-item')
    .filter((item) => item.id !== WRAPPER_ID && !item.hasAttribute?.('data-airyss-protection-wrap'));

  const getCartItemsList = () => {
    const root = upcartRoot();
    return root.querySelector?.('.upcart-public-cart-items')
      || document.querySelector('.upcart-public-cart-items');
  };

  const getUpCartBottomModules = () => {
    const root = upcartRoot();
    return root.querySelector?.('[class*="bottomModules"], [class*="BottomModules"]')
      || document.querySelector('[class*="bottomModules"], [class*="BottomModules"]');
  };

  // Always mount AFTER cart line items (never between products while Upcart is still loading).
  const buildPlacementWrapper = (card, parentHint) => {
    const parentIsList = parentHint?.tagName === 'UL' || parentHint?.tagName === 'OL'
      || parentHint?.parentElement?.tagName === 'UL' || parentHint?.parentElement?.tagName === 'OL';
    let wrap = ensureWrapper(card);
    if (parentIsList && wrap.tagName !== 'LI') {
      const li = document.createElement('li');
      li.id = WRAPPER_ID;
      li.className = 'airyss-protection-card-wrap upcart-public-cart-item';
      li.setAttribute('data-airyss-protection-wrap', 'true');
      li.style.cssText = 'list-style:none;display:block;order:9999;margin:0;padding:0;border:0;width:100%;';
      while (wrap.firstChild) li.appendChild(wrap.firstChild);
      if (wrap.id === WRAPPER_ID) wrap.remove();
      wrap = li;
      wrap.appendChild(card);
    } else {
      wrap.style.order = '9999';
    }
    return wrap;
  };

  const placeCardInUpCart = (card) => {
    const root = upcartRoot();
    ensureUpcartStyles(root);

    // During a stable hold, keep the existing mount — moving the node causes flicker.
    const existingWrap = root.getElementById?.(WRAPPER_ID) || document.getElementById(WRAPPER_ID);
    if (existingWrap?.isConnected && existingWrap.contains(card) && Date.now() < stableUiUntil) {
      return true;
    }

    const bottomModules = getUpCartBottomModules();
    const list = getCartItemsList();
    const items = getUpCartItems();
    const giftWrap = root.getElementById?.('airyss-included-gift-wrap')
      || document.getElementById('airyss-included-gift-wrap');

    // Prefer directly under the Included Free Gift card when present.
    if (giftWrap?.parentElement) {
      const wrap = buildPlacementWrapper(card, giftWrap.parentElement);
      if (wrap.parentElement === giftWrap.parentElement && wrap.previousElementSibling === giftWrap) {
        if (card.parentElement !== wrap) wrap.appendChild(card);
        return true;
      }
      if (wrap.previousElementSibling !== giftWrap || wrap.parentElement !== giftWrap.parentElement) {
        giftWrap.insertAdjacentElement('afterend', wrap);
      } else {
        wrap.appendChild(card);
      }
      return true;
    }

    // 1) Best: immediately before Upcart bottom modules (notes / Rx-ready / checkout area).
    if (bottomModules?.parentElement) {
      const wrap = buildPlacementWrapper(card, bottomModules.parentElement);
      if (wrap.nextElementSibling !== bottomModules || wrap.parentElement !== bottomModules.parentElement) {
        bottomModules.insertAdjacentElement('beforebegin', wrap);
      } else {
        wrap.appendChild(card);
      }
      return true;
    }

    // 2) Append as the last child of the cart items list.
    if (list) {
      const wrap = buildPlacementWrapper(card, list);
      if (wrap.parentElement !== list || list.lastElementChild !== wrap) {
        list.appendChild(wrap);
      } else {
        wrap.appendChild(card);
      }
      return true;
    }

    // 3) After the last rendered cart line item.
    if (items.length) {
      const last = items[items.length - 1];
      const wrap = buildPlacementWrapper(card, last.parentElement);
      if (wrap.previousElementSibling !== last || wrap.parentElement !== last.parentElement) {
        last.insertAdjacentElement('afterend', wrap);
      } else {
        wrap.appendChild(card);
      }
      return true;
    }

    return false;
  };

  const placeCard = (card) => {
    // Remove any stray copy left in the unused theme drawer.
    document.querySelectorAll(`cart-drawer #${CARD_ID}, #CartDrawer #${CARD_ID}, cart-drawer #${WRAPPER_ID}, #CartDrawer #${WRAPPER_ID}`).forEach((node) => {
      if (node !== card && !node.contains(card)) node.remove();
    });

    if (window.upcartDocumentOrShadowRoot || getUpCartItems().length || getCartItemsList()) {
      return placeCardInUpCart(card);
    }
    return false;
  };

  const hardHideNode = (node, className) => {
    if (!node || node.id === WRAPPER_ID || node.id === CARD_ID) return;
    if (node.id === 'airyss-included-gift-wrap' || node.id === 'airyss-included-gift-card') return;
    if (node.hasAttribute?.('data-airyss-protection-wrap') || node.hasAttribute?.('data-airyss-included-gift-wrap')) return;
    node.classList.add(className);
    node.style.setProperty('display', 'none', 'important');
    node.style.setProperty('visibility', 'hidden', 'important');
    node.setAttribute('aria-hidden', 'true');
  };

  const hideProtectionLineItems = (cart) => {
    const protection = cart?.items?.find?.(isProtectionItem);
    const variantId = protection
      ? String(protection.variant_id || protection.variantId)
      : String(cachedVariantId || config.variantId || '');
    const handle = String(config.productHandle || '2-year-accidental-protection-plan').toLowerCase();

    const tiles = queryAll(
      '.upcart-public-cart-item, .upcart-internal-cart-item, .upcart-public-component-product-tile, [class*="CartItem"], [class*="product-tile"]'
    );

    tiles.forEach((tile) => {
      if (tile.id === WRAPPER_ID || tile.hasAttribute('data-airyss-protection-wrap')) return;
      if (tile.closest?.(`#${CARD_ID}, #${WRAPPER_ID}, #airyss-included-gift-card, #airyss-included-gift-wrap`)) return;
      // Never hide the checkbox upsell card itself.
      if (tile.id === CARD_ID || tile.querySelector?.(`#${CARD_ID}, [data-protection-checkbox]`)) return;
      const text = (tile.textContent || '').toLowerCase();
      const html = (tile.innerHTML || '').toLowerCase();
      const titleMatch = /2-year accidental protection|accidental protection plan|airyss-protect-2yr/.test(text);
      const idMatch = variantId && html.includes(String(variantId).toLowerCase());
      const handleMatch = handle && html.includes(handle);
      const propMatch = /protection\s*plan\s*:\s*2-year/i.test(text);
      if (titleMatch || idMatch || handleMatch || propMatch) hardHideNode(tile, 'airyss-protection-line-hidden');
    });
  };

  const hideProtectionRowsNow = () => hideProtectionLineItems(null);

  // Keep Protected Delivery / 1-Year Warranty visible — they are intentional UpCart rewards.
  // When those products are removed in Shopify Admin, they will stop appearing on their own.
  const hideLegacyBenefitRows = () => {};

  const hideFreeGiftQuantityInUpcart = () => {
    const root = upcartRoot();
    ensureUpcartStyles(root);

    queryAll('.upcart-public-cart-item, .upcart-internal-cart-item').forEach((item) => {
      const text = item.textContent || '';
      const isGift = /Free Gift Included/i.test(text)
        || (/portable charger|magnetic charging cable/i.test(text) && /\$0|FREE|0\.00/i.test(text));
      if (!isGift) return;
      item.classList.add('upcart-free-gift-item');
      item.querySelectorAll(
        '.upcart-public-component-product-tile__quantity-selector, .upcart-public-component-product-tile__quantity-minus, .upcart-public-component-product-tile__quantity-plus'
      ).forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
      });
    });
  };

  // Force Ajax JSON endpoints (.js). Plain /cart/change can return HTML and break remove.
  const withJs = (url) => {
    const base = String(url || '');
    return base.endsWith('.js') ? base : `${base}.js`;
  };
  const cartChangeUrl = () => withJs(window.routes?.cart_change_url || '/cart/change');
  const cartUpdateUrl = () => withJs(window.routes?.cart_update_url || '/cart/update');
  const cartAddUrl = () => withJs(window.routes?.cart_add_url || '/cart/add');
  const cartJsonUrl = () => withJs(window.routes?.cart_url || '/cart');

  // Always use Shopify cart.js for mutations — Upcart's cart object often omits line keys.
  const getShopifyCart = () => fetch(cartJsonUrl(), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => r.json()).then((cart) => {
    lastCart = cart;
    return cart;
  });

  const postCartJson = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || `Cart request failed (${response.status})`);
    }
    return payload;
  });

  const setLineQuantity = (lineOrKey, quantity, lineNumber) => {
    const body = { quantity };
    if (lineNumber != null) body.line = Number(lineNumber);
    else body.id = lineOrKey;
    return postCartJson(cartChangeUrl(), body);
  };

  const lineKey = (line) => line.key || null;

  // Resolve the live storefront variant — theme settings can hold a stale ID.
  // Prefer cached/config IDs so checkbox clicks stay instant.
  const resolveProtectionVariantId = ({ requireAvailable = true } = {}) => {
    if (cachedVariantId) return Promise.resolve(cachedVariantId);
    if (config.variantId) {
      cachedVariantId = Number(config.variantId);
      return Promise.resolve(cachedVariantId);
    }

    const handle = config.productHandle || '2-year-accidental-protection-plan';
    return fetch(`${window.shopUrl || ''}/products/${handle}.js`)
      .then((response) => {
        if (!response.ok) throw new Error('Protection product not found on the storefront');
        return response.json();
      })
      .then((product) => {
        const variant = (product.variants || []).find((item) => item.available)
          || (product.variants || [])[0];
        if (!variant?.id) throw new Error('Protection product has no variant');
        config.variantId = variant.id;
        cachedVariantId = Number(variant.id);
        config.productAvailable = Boolean(variant.available);
        if (requireAvailable && !variant.available) {
          throw new Error('Protection plan is sold out. Enable “Continue selling when out of stock” on the protection product.');
        }
        return cachedVariantId;
      });
  };

  const addProtection = () => {
    if (busy) return;
    busy = true;
    handleProtectionActivate._busySince = Date.now();
    optimisticAdded = true;
    holdStableUi(2200);
    const card = ensureCard();
    // Instant UI — never wait on network before the checkbox looks selected.
    renderCardState(card, true);
    hideProtectionRowsNow();
    bumpVisibleTotals(lastProtectionCents);
    // Hold Cart · N at +1 so UpCart refresh can't flash 2 → 3.
    holdCartCount(1, 2400);

    const busyWatchdog = window.setTimeout(() => { busy = false; }, 7000);
    const cachedCart = lastCart || readUpcartCartSync();
    const cachedParent = cachedCart?.items?.find?.(isParentItem);
    const cachedId = Number(cachedVariantId || config.variantId);

    const doAdd = (cart, variantId) => {
      const parent = (cart?.items || []).find(isParentItem) || cachedParent;
      if (!parent) throw new Error('AIRYSS One is not in the cart');
      if ((cart?.items || []).some(isProtectionItem)) return cart;
      const id = Number(variantId || cachedId);
      if (!id) throw new Error('Protection plan variant is missing. Re-save Theme settings → Protection Plan.');
      return postCartJson(cartAddUrl(), {
        items: [{
          id,
          quantity: 1,
          properties: {
            [PROPERTY_KEY]: 'true',
            [PARENT_PROPERTY]: String(parent.variant_id || parent.variantId || parent.id),
            'Protection Plan': '2-Year Accidental',
          },
        }],
      });
    };

    // Prefer instant add from cache — avoid waiting on /cart.js before the request.
    const start = (cachedParent && cachedId)
      ? doAdd(cachedCart, cachedId)
      : Promise.all([getShopifyCart(), resolveProtectionVariantId({ requireAvailable: false })])
        .then(([cart, variantId]) => doAdd(cart, variantId));

    start
      .then((result) => {
        holdStableUi(2200);
        hideProtectionRowsNow();
        // Keep optimistic checked state; one soft refresh for totals only.
        if (result?.items) lastCart = result;
        paintProtectionUi(lastCart || cachedCart || { items: [] }, { preferOptimistic: true });
        refreshCartDrawers({ soft: true });
        [60, 200].forEach((ms) => window.setTimeout(hideProtectionRowsNow, ms));
        // Delayed truth sync — do not remount the card immediately.
        window.setTimeout(() => {
          if (busy) return;
          getShopifyCart().then((cart) => {
            lastCart = cart;
            const protection = cart.items.find(isProtectionItem);
            if (protection?.final_price != null) lastProtectionCents = Number(protection.final_price) || lastProtectionCents;
            paintProtectionUi(cart, { preferOptimistic: false });
            if (window.AiryssCartCount?.hold) {
              window.AiryssCartCount.hold(window.AiryssCartCount.displayCount(cart), 800);
            }
            window.setTimeout(releaseCartCountHold, 900);
          }).catch(() => {});
        }, 500);
      })
      .catch((error) => {
        console.error('Protection plan add failed:', error);
        optimisticAdded = false;
        bumpVisibleTotals(-lastProtectionCents);
        holdCartCount(-1, 500);
        renderCardState(ensureCard(), false);
        showCardError(error?.message || 'Could not add protection plan');
        window.setTimeout(releaseCartCountHold, 600);
      })
      .finally(() => {
        window.clearTimeout(busyWatchdog);
        busy = false;
        // Keep optimistic flag briefly so a late UpCart repaint can't uncheck.
        window.setTimeout(() => { optimisticAdded = null; }, 400);
      });
  };

  const showCardError = (message) => {
    const cardEl = ensureCard();
    let notice = cardEl.querySelector('[data-protection-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.setAttribute('data-protection-error', 'true');
      notice.style.cssText = 'margin:.75rem 0 0;color:#b42318;font-size:.85rem;line-height:1.35;';
      cardEl.appendChild(notice);
    }
    notice.textContent = message;
  };

  const removeProtection = () => {
    if (busy) return;
    busy = true;
    handleProtectionActivate._busySince = Date.now();
    optimisticAdded = false;
    holdStableUi(2200);
    const card = ensureCard();
    // Instant UI — uncheck immediately while the cart request runs.
    renderCardState(card, false);
    bumpVisibleTotals(-lastProtectionCents);
    holdCartCount(-1, 2400);
    const busyWatchdog = window.setTimeout(() => { busy = false; }, 7000);

    // Do not require availability — Unlisted / search-hidden products must still be removable.
    getShopifyCart()
      .then((cart) => {
        const updates = {};
        let matched = 0;
        cart.items.forEach((item) => {
          if (isProtectionItem(item)) {
            matched += 1;
            if (item.key) updates[item.key] = 0;
            else updates[String(item.variant_id || item.id)] = 0;
            if (item.final_price != null) lastProtectionCents = Number(item.final_price) || lastProtectionCents;
          }
        });

        if (matched) {
          return postCartJson(cartUpdateUrl(), { updates });
        }

        // Fallback if keys missing / properties stripped: zero the variant id.
        if (cachedVariantId || config.variantId) {
          return postCartJson(cartUpdateUrl(), {
            updates: { [String(cachedVariantId || config.variantId)]: 0 },
          });
        }

        // Last resort: change by line number for title/handle matches.
        const targets = cart.items
          .map((item, index) => ({ item, line: index + 1 }))
          .filter(({ item }) => isProtectionItem(item));
        return targets.reverse().reduce(
          (promise, { item, line }) => promise.then(() => {
            const key = lineKey(item);
            if (key) return setLineQuantity(key, 0);
            return setLineQuantity(item.variant_id || item.id, 0, line);
          }),
          Promise.resolve()
        );
      })
      .then(() => {
        holdStableUi(2200);
        hideProtectionRowsNow();
        refreshCartDrawers({ soft: true });
        window.setTimeout(() => {
          if (busy) return;
          getShopifyCart().then((cart) => {
            lastCart = cart;
            paintProtectionUi(cart, { preferOptimistic: false });
            if (window.AiryssCartCount?.hold) {
              window.AiryssCartCount.hold(window.AiryssCartCount.displayCount(cart), 800);
            }
            window.setTimeout(releaseCartCountHold, 900);
          }).catch(() => {});
        }, 400);
      })
      .catch((error) => {
        console.error('Protection plan remove failed:', error);
        optimisticAdded = true;
        bumpVisibleTotals(lastProtectionCents);
        holdCartCount(1, 500);
        renderCardState(ensureCard(), true);
        showCardError(error?.message || 'Could not remove protection plan');
        window.setTimeout(releaseCartCountHold, 600);
      })
      .finally(() => {
        window.clearTimeout(busyWatchdog);
        busy = false;
        window.setTimeout(() => { optimisticAdded = null; }, 400);
      });
  };

  const removeOrphanedProtection = (cart) => {
    const hasParent = cart.items.some(isParentItem);
    const protectionLines = cart.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => isProtectionItem(item));
    const ops = [];

    if (!hasParent) {
      protectionLines.slice().reverse().forEach(({ item, line }) => {
        const key = lineKey(item);
        ops.push(key ? setLineQuantity(key, 0) : setLineQuantity(item.variant_id || item.id, 0, line));
      });
    } else if (protectionLines.length > 1) {
      protectionLines.slice(1).reverse().forEach(({ item, line }) => {
        const key = lineKey(item);
        ops.push(key ? setLineQuantity(key, 0) : setLineQuantity(item.variant_id || item.id, 0, line));
      });
      if (protectionLines[0].item.quantity > 1) {
        const first = protectionLines[0];
        const key = lineKey(first.item);
        ops.push(key ? setLineQuantity(key, 1) : setLineQuantity(first.item.variant_id || first.item.id, 1, first.line));
      }
    } else if (protectionLines[0]?.item.quantity > 1) {
      const first = protectionLines[0];
      const key = lineKey(first.item);
      ops.push(key ? setLineQuantity(key, 1) : setLineQuantity(first.item.variant_id || first.item.id, 1, first.line));
    }

    if (!ops.length) return Promise.resolve(false);
    return Promise.all(ops).then(() => true);
  };

  const paintProtectionUi = (cart, { preferOptimistic = false } = {}) => {
    if (!cart?.items) return false;
    lastCart = cart;
    bindUpcartApi();
    observeShadowRoot();
    const root = upcartRoot();
    ensureUpcartStyles(root);
    bindProtectionActivation(document);
    bindProtectionActivation(root);
    hideProtectionLineItems(cart);

    const hasParent = cart.items.some(isParentItem);
    const protection = cart.items.find(isProtectionItem);
    const card = ensureCard();
    const holding = Date.now() < stableUiUntil || busy;

    if (!hasParent) {
      // During add hold, don't tear the card down on a stale cart snapshot.
      if (holding && optimisticAdded) {
        hideProtectionLineItems(cart);
        return true;
      }
      card.hidden = true;
      upcartRoot().getElementById?.(WRAPPER_ID)?.remove();
      document.getElementById(WRAPPER_ID)?.remove();
      return true;
    }

    // Stable path: card already mounted — update state only (no remount flicker).
    const wrap = root.getElementById?.(WRAPPER_ID) || document.getElementById(WRAPPER_ID);
    if (holding && wrap?.isConnected && wrap.contains(card) && !card.hidden) {
      const addedState = optimisticAdded != null ? optimisticAdded : Boolean(protection);
      renderCardState(card, addedState);
      hideProtectionLineItems(cart);
      return true;
    }

    // Place as soon as any cart mount point exists — don't wait for bottom modules.
    const canMount = Boolean(
      getUpCartBottomModules()
      || getCartItemsList()
      || getUpCartItems().length
      || upcartRoot().querySelector?.('.upcart-public-cart-items, [class*="bottomModules"]')
    );
    if (!canMount) {
      // Keep trying on the next tick while UpCart finishes painting.
      window.setTimeout(() => {
        if (lastCart) paintProtectionUi(lastCart, { preferOptimistic });
      }, 50);
      return false;
    }

    const placed = placeCard(card);
    if (!placed) return false;

    const addedState = preferOptimistic && optimisticAdded != null
      ? optimisticAdded
      : (optimisticAdded != null && holding ? optimisticAdded : Boolean(protection));
    if (!(busy && optimisticAdded != null) || preferOptimistic) {
      renderCardState(card, addedState);
    }
    card.hidden = false;
    hideProtectionLineItems(cart);
    return true;
  };

  const schedulePaint = (cart, delay = 0) => {
    window.clearTimeout(paintTimer);
    paintTimer = window.setTimeout(() => paintProtectionUi(cart || lastCart || readUpcartCartSync()), delay);
  };

  const sync = ({ preferOptimistic = false } = {}) => {
    bindUpcartApi();
    observeShadowRoot();
    ensureUpcartStyles(upcartRoot());
    hideProtectionRowsNow();

    // While add/remove is in flight, only keep the optimistic checkbox UI —
    // never orphan-clean or overwrite the checked state from a stale cart.
    if (busy) {
      const instantBusy = readUpcartCartSync() || lastCart;
      if (instantBusy) paintProtectionUi(instantBusy, { preferOptimistic: true });
      return Promise.resolve(instantBusy);
    }

    // Instant paint from in-memory UpCart / last cart — don't wait on network.
    const instant = readUpcartCartSync() || lastCart;
    if (instant) paintProtectionUi(instant, { preferOptimistic });

    return getShopifyCart()
      .then((cart) => {
        if (busy) {
          paintProtectionUi(cart, { preferOptimistic: true });
          return cart;
        }
        lastCart = cart;
        paintProtectionUi(cart, { preferOptimistic });
        return removeOrphanedProtection(cart).then((removed) => {
          if (removed) {
            refreshCartDrawers({ soft: true });
            return getShopifyCart().then((fresh) => {
              lastCart = fresh;
              paintProtectionUi(fresh, { preferOptimistic });
              return fresh;
            });
          }
          return cart;
        });
      })
      .catch((error) => console.error('Protection plan sync failed:', error));
  };

  const scheduleSync = (delay = 0) => {
    if (busy) {
      hideProtectionRowsNow();
      schedulePaint(null, 0);
      return;
    }
    // Always try an instant paint first on open/update.
    schedulePaint(readUpcartCartSync() || lastCart, 0);
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(sync, delay);
  };

  const bindUpcartApi = () => {
    if (upcartApiBound) return;
    const subs = [
      'upcartSubscribeCartOpened',
      'upcartSubscribeCartLoaded',
      'upcartSubscribeCartUpdated',
      'upcartSubscribeAddedToCart',
      'upcartSubscribeItemRemoved',
    ];
    const available = subs.filter((name) => typeof window[name] === 'function');
    if (!available.length) return;
    available.forEach((name) => {
      try {
        window[name](() => {
          if (name.includes('ItemRemoved')) {
            window.setTimeout(() => sync({ preferOptimistic: false }), 120);
            window.setTimeout(() => sync({ preferOptimistic: false }), 500);
          }
          // During protection add/remove hold, only hide native lines — no full sync flicker.
          if (busy || Date.now() < stableUiUntil) {
            hideProtectionRowsNow();
            schedulePaint(readUpcartCartSync() || lastCart, 0);
            return;
          }
          // Open/load must paint on the same turn whenever possible.
          scheduleSync(name.includes('Opened') || name.includes('Loaded') ? 0 : 40);
        });
      } catch (error) { /* ignore */ }
    });
    upcartApiBound = true;
  };

  const observeShadowRoot = () => {
    const root = window.upcartDocumentOrShadowRoot;
    bindProtectionActivation(document);
    if (root && root !== document) bindProtectionActivation(root);
    if (shadowObserverBound) return;
    if (!root || root === document) return;
    new MutationObserver(() => {
      hideProtectionRowsNow();
      if (busy || Date.now() < stableUiUntil) {
        // Re-attach listeners only — do not remount the card while totals refresh.
        const live = upcartRoot().getElementById?.(CARD_ID);
        if (live) wireCardActivation(live);
        return;
      }
      schedulePaint(readUpcartCartSync() || lastCart, 0);
      if (!busy) scheduleSync(160);
    }).observe(root, { childList: true, subtree: true });
    shadowObserverBound = true;
    ensureUpcartStyles(root);
    schedulePaint(readUpcartCartSync() || lastCart, 0);
  };

  window.addEventListener('airyss:parent-removed', () => {
    // Parent deleted — drop protection with the free gift.
    sync({ preferOptimistic: false });
  });

  // Hunt UpCart shadow root quickly on slow devices.
  let shadowHuntCount = 0;
  const shadowHuntTimer = window.setInterval(() => {
    shadowHuntCount += 1;
    bindUpcartApi();
    observeShadowRoot();
    if (shadowObserverBound || shadowHuntCount > 80) window.clearInterval(shadowHuntTimer);
  }, 100);

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const requestUrl = String(args[0]?.url || args[0] || '');
    if (/\/cart\/(add|change|update|clear)/.test(requestUrl) && !busy) {
      // Re-paint as soon as the cart response lands.
      scheduleSync(0);
    }
    return response;
  };

  document.addEventListener('click', (event) => {
    if (busy) return;
    // Never re-sync from a protection-card click — that race was wiping the checkbox.
    if (findProtectionCard(event)) return;
    if (event.target.closest?.('.upcart-cart, [class*="upcart"], cart-drawer, #CartDrawer, [aria-controls*="cart" i], [class*="cart-icon" i]')) {
      scheduleSync(0);
    }
  }, true);

  // Window capture for open shadow roots; pointerup often beats apps that block click.
  window.addEventListener('click', handleProtectionActivate, true);
  window.addEventListener('pointerup', handleProtectionActivate, true);

  window.setInterval(() => {
    bindUpcartApi();
    observeShadowRoot();
    const liveCard = upcartRoot().getElementById?.(CARD_ID) || document.getElementById(CARD_ID);
    if (liveCard) wireCardActivation(liveCard);
  }, 2000);

  // Prefetch cart + warm protection variant so first open is instant.
  bindProtectionActivation(document);
  resolveProtectionVariantId({ requireAvailable: false }).catch(() => {});
  getShopifyCart().then((cart) => {
    lastCart = cart;
    schedulePaint(cart, 0);
  }).catch(() => {});

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleSync(0));
  } else {
    scheduleSync(0);
  }
})();
